import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, startOfWeek, endOfWeek, subWeeks, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import jsPDF from 'jspdf';
import { formatCurrency, getEcuadorDate } from '../../utils/reportUtils';
import printerService from '../../services/printerService';

const getFastFoodBaseURL = () => {
    return process.env.REACT_APP_FAST_FOOD_SERVICE || process.env.REACT_APP_API_BASE_URL || '';
};

const ReportesPeriodicos = () => {
    const [activeTab, setActiveTab] = useState('semanales'); // 'semanales' | 'mensuales'
    const [selectedDate, setSelectedDate] = useState(getEcuadorDate());
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');
    const exchangeRate = parseFloat(localStorage.getItem('usdExchangeRate')) || 4000;

    // Quick select options
    const pastWeeks = Array.from({ length: 4 }).map((_, i) => subWeeks(getEcuadorDate(), i));
    const pastMonths = Array.from({ length: 4 }).map((_, i) => subMonths(getEcuadorDate(), i));

    const generateReportData = async (date, type) => {
        setLoading(true);
        setError('');
        setReport(null);
        
        try {
            const payload = {
                report_type: type === 'semanales' ? 'weekly' : 'monthly',
                include_orders_detail: true
            };

            if (type === 'semanales') {
                payload.start_date = format(startOfWeek(date, { locale: es }), 'yyyy-MM-dd');
                payload.end_date = format(endOfWeek(date, { locale: es }), 'yyyy-MM-dd');
            } else {
                payload.year = date.getFullYear();
                payload.month = date.getMonth() + 1;
            }

            const response = await api.post('/api/pos/daily-summaries/get_report/', payload, {
                baseURL: getFastFoodBaseURL(),
                timeout: 30000
            });

            setReport(response.data.data || response.data);
        } catch (err) {
            console.error('Error generating periodic report:', err);
            setError(err.response?.data?.detail || 'Error al generar el reporte. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    // Auto-generate on tab/date change if it's from quick select
    const handleQuickSelect = (date) => {
        setSelectedDate(date);
        generateReportData(date, activeTab);
    };

    const handlePrintA4PDF = () => {
        if (!report) return;
        
        let dateRangeStr = '';
        if (activeTab === 'semanales') {
            const start = format(startOfWeek(selectedDate, { locale: es }), 'dd/MM/yyyy');
            const end = format(endOfWeek(selectedDate, { locale: es }), 'dd/MM/yyyy');
            dateRangeStr = `Fechas: ${start} al ${end}`;
        } else {
            const monthName = format(selectedDate, 'MMMM yyyy', { locale: es });
            dateRangeStr = `Mes: ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
        }

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.text("Reportes Periódicos", 15, 20);
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(dateRangeStr, 15, 30);

        const colWidth = 130;
        const leftX = 15;
        const rightX = 150;
        let startY = 45;

        // --- COLUMNA USD ---
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.5);
        doc.line(leftX, startY + 2, leftX + colWidth, startY + 2);
        
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(31, 41, 55);
        doc.text("Dólares (USD)", leftX, startY);

        let yL = startY + 15;
        doc.setFontSize(12);
        doc.setTextColor(107, 114, 128);
        doc.text("Recaudado Neto", leftX, yL);
        doc.text("Gastos de la Semana", leftX + 65, yL);
        yL += 8;
        doc.setFontSize(16);
        doc.setTextColor(29, 78, 216);
        doc.text(formatCurrency(totalUSD), leftX, yL);
        doc.setTextColor(239, 68, 68);
        doc.text(formatCurrency(totalExpenses), leftX + 65, yL);
        
        yL += 15;
        doc.setFontSize(14);
        doc.setTextColor(31, 41, 55);
        doc.text("Productos más vendidos", leftX, yL);
        yL += 7;
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        sortedProductsUSD.forEach(([name, qty]) => {
            doc.text(name, leftX, yL);
            doc.text(`${qty} un`, leftX + 110, yL, { align: 'right' });
            yL += 6;
        });

        yL += 10;
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Días de mayores ingresos", leftX, yL);
        yL += 7;
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        sortedDaysUSD.forEach(([date, amt]) => {
            doc.text(date, leftX, yL);
            doc.text(formatCurrency(amt), leftX + 110, yL, { align: 'right' });
            yL += 6;
        });

        // --- COLUMNA COP ---
        doc.setDrawColor(34, 197, 94);
        doc.line(rightX, startY + 2, rightX + colWidth, startY + 2);
        
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(22, 101, 52);
        doc.text("Pesos (COP)", rightX, startY);

        let yR = startY + 15;
        doc.setFontSize(12);
        doc.text("Recaudado Total", rightX, yR);
        yR += 8;
        doc.setFontSize(16);
        doc.text(`$${totalCOP.toLocaleString('es-CO')} COP`, rightX, yR);
        
        yR += 15;
        doc.setFontSize(14);
        doc.text("Productos más vendidos", rightX, yR);
        yR += 7;
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        sortedProductsCOP.forEach(([name, qty]) => {
            doc.text(name, rightX, yR);
            doc.text(`${qty} un`, rightX + 110, yR, { align: 'right' });
            yR += 6;
        });

        yR += 10;
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Días de mayores ingresos", rightX, yR);
        yR += 7;
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        sortedDaysCOP.forEach(([date, amt]) => {
            doc.text(date, rightX, yR);
            doc.text(`$${Math.round(amt).toLocaleString('es-CO')}`, rightX + 110, yR, { align: 'right' });
            yR += 6;
        });

        doc.autoPrint();
        window.open(doc.output('bloburl'), '_blank');
    };

    const handleThermalPrint = async () => {
        if (!report) return;
        
        try {
            let dateRangeStr = '';
            let title = '';
            if (activeTab === 'semanales') {
                const start = format(startOfWeek(selectedDate, { locale: es }), 'dd/MM/yyyy');
                const end = format(endOfWeek(selectedDate, { locale: es }), 'dd/MM/yyyy');
                dateRangeStr = `${start} - ${end}`;
                title = 'REPORTE SEMANAL';
            } else {
                const monthName = format(selectedDate, 'MMMM yyyy', { locale: es });
                dateRangeStr = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                title = 'REPORTE MENSUAL';
            }

            const chars_per_line = 32;
            const centerText = (text) => {
                if (text.length >= chars_per_line) return text.substring(0, chars_per_line);
                const padding = Math.floor((chars_per_line - text.length) / 2);
                return " ".repeat(padding) + text;
            };
            const rightAlign = (label, value) => {
                const labelStr = String(label);
                const valueStr = String(value);
                const spaces = chars_per_line - labelStr.length - valueStr.length;
                if (spaces > 0) return labelStr + " ".repeat(spaces) + valueStr;
                return labelStr.substring(0, chars_per_line - valueStr.length - 1) + " " + valueStr;
            };

            let lines = [];
            lines.push(centerText(title));
            lines.push(centerText(dateRangeStr));
            lines.push("-".repeat(chars_per_line));
            lines.push(centerText("REPORTE EN DOLARES"));
            lines.push("-".repeat(chars_per_line));
            lines.push(rightAlign("Recaudado:", `${formatCurrency(totalUSD)}`));
            lines.push(rightAlign("Gastos:", `${formatCurrency(totalExpenses)}`));
            lines.push("");
            lines.push("PRODUCTOS MAS VENDIDOS:");
            sortedProductsUSD.slice(0, 5).forEach(([name, qty]) => {
                lines.push(rightAlign(name, `${qty} un`));
            });
            lines.push("");
            lines.push("-".repeat(chars_per_line));
            lines.push(centerText("REPORTE EN PESOS (COP)"));
            lines.push("-".repeat(chars_per_line));
            lines.push(rightAlign("Recaudado:", `$${totalCOP.toLocaleString('es-CO')}`));
            lines.push("");
            lines.push("PRODUCTOS MAS VENDIDOS:");
            sortedProductsCOP.slice(0, 5).forEach(([name, qty]) => {
                lines.push(rightAlign(name, `${qty} un`));
            });
            lines.push("-".repeat(chars_per_line));
            lines.push(centerText("FIN DEL REPORTE"));
            lines.push("\n\n\n");

            const ticketContent = lines.join("\n");
            await printerService.printCustomTicket(ticketContent, 'report');
            alert('✅ Reporte enviado a la impresora térmica.');
        } catch (error) {
            console.error('Error printing thermal report:', error);
            alert('⚠️ Error al enviar el reporte a la impresora térmica. Verifica el agente.');
        }
    };

    // Extraer estadisticas base
    const totalSales = report ? parseFloat(report.total_sales || 0) : 0;
    const totalExpenses = report ? parseFloat(report.total_expenses || 0) : 0;
    
    let efectivoUSD = 0;
    let transferenciaUSD = 0;
    let efectivoCOPInUSD = 0;
    let transferenciaCOPInUSD = 0;

    let productsUSD = {};
    let productsCOP = {};
    let daysUSD = {};
    let daysCOP = {};

    if (report && report.orders_detail) {
        report.orders_detail.forEach(order => {
            const method = String(order.payment_method_display || order.payment_method || '').toLowerCase();
            const amount = parseFloat(order.total_amount || order.total || 0);

            if (method === 'efectivo_dolares' || method.includes('cash') || (method.includes('efectivo') && !method.includes('cop'))) efectivoUSD += amount;
            else if (method === 'transferencia_dolares' || (method.includes('transferencia') && !method.includes('cop'))) transferenciaUSD += amount;
            else if (method === 'efectivo_cop' || (method.includes('efectivo') && method.includes('cop'))) efectivoCOPInUSD += amount;
            else if (method === 'transferencia_cop' || (method.includes('transferencia') && method.includes('cop'))) transferenciaCOPInUSD += amount;

            const isCop = method.includes('cop');
            
            const dateVal = order.timestamp || order.created_at || report.date;
            const dateObj = dateVal ? new Date(dateVal) : new Date();
            const dateStr = isNaN(dateObj) ? 'N/A' : format(dateObj, 'dd MMM yyyy', { locale: es });
            
            if (isCop) {
                daysCOP[dateStr] = (daysCOP[dateStr] || 0) + (amount * exchangeRate);
                (order.items || []).forEach(item => {
                    const pName = item.product_details?.name || 'Desconocido';
                    productsCOP[pName] = (productsCOP[pName] || 0) + (item.quantity || 1);
                });
            } else {
                daysUSD[dateStr] = (daysUSD[dateStr] || 0) + amount;
                (order.items || []).forEach(item => {
                    const pName = item.product_details?.name || 'Desconocido';
                    productsUSD[pName] = (productsUSD[pName] || 0) + (item.quantity || 1);
                });
            }
        });
    }

    let expensesUSDList = [];
    let expensesCOPList = [];
    let totalExpensesUSD = 0;
    let totalExpensesCOP = 0;

    if (report && report.expenses_list) {
        report.expenses_list.forEach(exp => {
            const isCop = String(exp.description || '').toLowerCase().includes('cop') || String(exp.description || '').toLowerCase().includes('pesos');
            const amt = parseFloat(exp.amount || 0);
            if (isCop) {
                expensesCOPList.push(exp);
                totalExpensesCOP += (amt * exchangeRate);
            } else {
                expensesUSDList.push(exp);
                totalExpensesUSD += amt;
            }
        });
    }

    const totalCOP = (efectivoCOPInUSD + transferenciaCOPInUSD) * exchangeRate;
    const totalUSD = efectivoUSD + transferenciaUSD;

    const sortedProductsUSD = Object.entries(productsUSD).sort((a,b) => b[1] - a[1]).slice(0, 10);
    const sortedProductsCOP = Object.entries(productsCOP).sort((a,b) => b[1] - a[1]).slice(0, 10);
    const sortedDaysUSD = Object.entries(daysUSD).sort((a,b) => b[1] - a[1]).slice(0, 5);
    const sortedDaysCOP = Object.entries(daysCOP).sort((a,b) => b[1] - a[1]).slice(0, 5);

    return (
        <div style={{ padding: '20px', backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '20px' }}>
                Reportes Periódicos
            </h1>

            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '20px' }}>
                    <button
                        onClick={() => { setActiveTab('semanales'); setReport(null); setError(''); }}
                        style={{
                            padding: '10px 20px',
                            fontWeight: 'bold',
                            borderBottom: activeTab === 'semanales' ? '3px solid #3b82f6' : '3px solid transparent',
                            color: activeTab === 'semanales' ? '#3b82f6' : '#6b7280',
                            backgroundColor: 'transparent',
                            borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        Reportes Semanales
                    </button>
                    <button
                        onClick={() => { setActiveTab('mensuales'); setReport(null); setError(''); }}
                        style={{
                            padding: '10px 20px',
                            fontWeight: 'bold',
                            borderBottom: activeTab === 'mensuales' ? '3px solid #3b82f6' : '3px solid transparent',
                            color: activeTab === 'mensuales' ? '#3b82f6' : '#6b7280',
                            backgroundColor: 'transparent',
                            borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        Reportes Mensuales
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                            {activeTab === 'semanales' ? 'Seleccionar Semana' : 'Seleccionar Mes'}
                        </h3>
                        {activeTab === 'semanales' && (
                            <div style={{ marginBottom: '10px', fontSize: '14px', color: '#4b5563', backgroundColor: '#e5e7eb', padding: '8px', borderRadius: '4px' }}>
                                <strong>Rango seleccionado:</strong><br/>
                                Del {format(startOfWeek(selectedDate, { locale: es }), 'dd MMM yyyy')} al {format(endOfWeek(selectedDate, { locale: es }), 'dd MMM yyyy')}
                            </div>
                        )}
                        {activeTab === 'mensuales' && (
                            <div style={{ marginBottom: '10px', fontSize: '14px', color: '#4b5563', backgroundColor: '#e5e7eb', padding: '8px', borderRadius: '4px' }}>
                                <strong>Mes seleccionado:</strong><br/>
                                <span style={{ textTransform: 'capitalize' }}>{format(selectedDate, 'MMMM yyyy', { locale: es })}</span>
                            </div>
                        )}
                        
                        <div style={{ marginBottom: '15px' }}>
                            <DatePicker
                                selected={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                                showMonthYearPicker={activeTab === 'mensuales'}
                                inline
                                locale={es}
                            />
                        </div>
                        <button 
                            onClick={() => generateReportData(selectedDate, activeTab)}
                            disabled={loading}
                            style={{ 
                                width: '100%', padding: '10px', backgroundColor: '#10b981', color: 'white', 
                                border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {loading ? 'Generando...' : 'Generar Reporte'}
                        </button>
                    </div>

                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>Historial Rápido</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {activeTab === 'semanales' && pastWeeks.map((date, idx) => {
                                const start = format(startOfWeek(date, { locale: es }), 'dd MMM');
                                const end = format(endOfWeek(date, { locale: es }), 'dd MMM, yyyy');
                                return (
                                    <button 
                                        key={idx} 
                                        onClick={() => handleQuickSelect(date)}
                                        style={{ padding: '12px', textAlign: 'left', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}
                                    >
                                        Semana del {start} al {end}
                                    </button>
                                );
                            })}
                            {activeTab === 'mensuales' && pastMonths.map((date, idx) => {
                                const monthName = format(date, 'MMMM yyyy', { locale: es });
                                return (
                                    <button 
                                        key={idx} 
                                        onClick={() => handleQuickSelect(date)}
                                        style={{ padding: '12px', textAlign: 'left', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontWeight: '500', textTransform: 'capitalize' }}
                                    >
                                        {monthName}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {report && (
                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827' }}>
                            Resumen {activeTab === 'semanales' ? `del ${format(startOfWeek(selectedDate, { locale: es }), 'dd MMM')} al ${format(endOfWeek(selectedDate, { locale: es }), 'dd MMM')}` : `de ${format(selectedDate, 'MMM yyyy', { locale: es })}`}
                        </h2>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                onClick={handlePrintA4PDF}
                                style={{ 
                                    padding: '10px 20px', backgroundColor: '#ef4444', color: 'white', 
                                    border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                }}
                            >
                                <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                                Imprimir Formato PDF
                            </button>
                            <button 
                                onClick={handleThermalPrint}
                                style={{ 
                                    padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white', 
                                    border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                }}
                            >
                                <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                                Imprimir en Caja (Térmica)
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        {/* LEFT COLUMN: USD */}
                        <div style={{ backgroundColor: '#f9fafb', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', marginBottom: '15px', borderBottom: '2px solid #3b82f6', paddingBottom: '5px' }}>Dólares (USD)</h3>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <div style={{ padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Recaudado Neto</div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1d4ed8' }}>{formatCurrency(totalUSD)}</div>
                                </div>
                                <div style={{ padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Gastos de la Semana</div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>{formatCurrency(totalExpensesUSD)}</div>
                                </div>
                            </div>

                            <div style={{ padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '20px' }}>
                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155', marginBottom: '10px' }}>Detalle de Gastos</div>
                                {expensesUSDList.length > 0 ? (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px' }}>
                                        {expensesUSDList.map((exp, idx) => (
                                            <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                                                <span>{exp.description} <span style={{ fontSize: '11px', color: '#94a3b8' }}>({exp.date})</span></span>
                                                <span style={{ fontWeight: '500', color: '#ef4444' }}>-{formatCurrency(exp.amount)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>No hay gastos en USD</div>
                                )}
                            </div>

                            <div style={{ padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '20px' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>Productos más vendidos</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px' }}>
                                    {sortedProductsUSD.length > 0 ? sortedProductsUSD.map(([name, qty], i) => (
                                        <li key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', padding: '6px 0' }}>
                                            <span style={{ color: '#374151' }}>{name}</span>
                                            <span style={{ fontWeight: 'bold', color: '#111827' }}>{qty} un</span>
                                        </li>
                                    )) : <li style={{ color: '#9ca3af', padding: '4px 0' }}>No hay ventas en USD</li>}
                                </ul>
                            </div>

                            <div style={{ backgroundColor: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>Días de mayores ingresos</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px' }}>
                                    {sortedDaysUSD.length > 0 ? sortedDaysUSD.map(([date, amt], i) => (
                                        <li key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', padding: '6px 0' }}>
                                            <span style={{ color: '#374151' }}>{date}</span>
                                            <span style={{ fontWeight: 'bold', color: '#111827' }}>{formatCurrency(amt)}</span>
                                        </li>
                                    )) : <li style={{ color: '#9ca3af', padding: '4px 0' }}>No hay datos</li>}
                                </ul>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: COP */}
                        <div style={{ backgroundColor: '#f0fdf4', padding: '20px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#166534', marginBottom: '15px', borderBottom: '2px solid #22c55e', paddingBottom: '5px' }}>Pesos (COP)</h3>
                            
                            <div style={{ padding: '15px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', marginBottom: '20px' }}>
                                <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>Recaudado Total</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#15803d' }}>
                                    ${totalCOP.toLocaleString('es-CO')} COP
                                </div>
                            </div>

                            <div style={{ padding: '15px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', marginBottom: '20px' }}>
                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#166534', marginBottom: '10px' }}>Detalle de Gastos</div>
                                {expensesCOPList.length > 0 ? (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px' }}>
                                        {expensesCOPList.map((exp, idx) => (
                                            <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #bbf7d0', color: '#166534' }}>
                                                <span>{exp.description} <span style={{ fontSize: '11px', color: '#86efac' }}>({exp.date})</span></span>
                                                <span style={{ fontWeight: '500', color: '#ef4444' }}>-${(parseFloat(exp.amount)*exchangeRate).toLocaleString('es-CO')} COP</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div style={{ fontSize: '13px', color: '#86efac' }}>No hay gastos en COP</div>
                                )}
                            </div>

                            <div style={{ padding: '15px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', marginBottom: '20px' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: '#166534', marginBottom: '8px' }}>Productos más vendidos</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#166534' }}>
                                    {sortedProductsCOP.length > 0 ? sortedProductsCOP.map(([name, qty], i) => (
                                        <li key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0fdf4', padding: '6px 0' }}>
                                            <span>{name}</span>
                                            <span style={{ fontWeight: 'bold' }}>{qty} un</span>
                                        </li>
                                    )) : <li style={{ color: '#9ca3af', padding: '4px 0' }}>No hay ventas en COP</li>}
                                </ul>
                            </div>

                            <div style={{ backgroundColor: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: '#166534', marginBottom: '8px' }}>Días de mayores ingresos</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#166534' }}>
                                    {sortedDaysCOP.length > 0 ? sortedDaysCOP.map(([date, amt], i) => (
                                        <li key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0fdf4', padding: '6px 0' }}>
                                            <span>{date}</span>
                                            <span style={{ fontWeight: 'bold' }}>${Math.round(amt).toLocaleString('es-CO')}</span>
                                        </li>
                                    )) : <li style={{ color: '#9ca3af', padding: '4px 0' }}>No hay datos</li>}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportesPeriodicos;
